import React, { useState, useEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Table, Typography, Flex, Tag, Form, Spin } from "antd"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import { DeleteOutlined, CheckOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import ExperienceSelector from "pagesV2/pro/Equipment/experience/ExperienceSelector"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { getTrips } from "store/boats"
import { toaster } from "components/common/Toast"
import styles from "./index.module.css"

/**
 * Shared modal for assigning experiences to equipment or crew members
 * @param {boolean} open - Controls modal visibility
 * @param {function} onCancel - Callback when modal is closed
 * @param {function} onSave - Callback when save is clicked, receives selectedExperienceIds array
 * @param {array} currentExperiences - Currently assigned experiences
 * @param {string} emptyText - Text to show when no experiences are selected
 * @param {boolean} loading - Whether currently loading assigned experiences data
 */
const AssignExperiencesModal = ({ open, onCancel, onSave, currentExperiences = [], emptyText, loading = false }) => {
  const { Title } = Typography
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [selectedExperiences, setSelectedExperiences] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const prevOpenRef = useRef(false)
  const prevLoadingRef = useRef(loading)

  // Get experiences from Redux store
  const tripsState = useSelector((state) => state?.trips?.items || [])

  // Memoized experiences list
  const experiencesMemo = useMemo(
    () =>
      tripsState.map((trip) => ({
        id: String(trip.id),
        name: trip.title || "Untitled",
        guests: trip.max_guests || 0,
        price: trip.min_price ? Number(trip.min_price) : 0,
        status: trip.status,
        currency: "$"
      })),
    [tripsState]
  )

  // Derive selected experience data for table
  const selectedExperienceData = useMemo(
    () => (selectedExperiences.length ? experiencesMemo.filter((exp) => selectedExperiences.includes(String(exp.id))) : []),
    [selectedExperiences, experiencesMemo]
  )

  // Fetch experiences on mount
  useEffect(() => {
    dispatch(getTrips())
  }, [dispatch])

  // Clear form when modal opens
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Modal just opened - clear form and wait for fresh data to load
      setSelectedExperiences([])
      form.resetFields()
    }
  }, [open, form])

  // Initialize with fresh data when loading completes OR when data is available without loading
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false
      return
    }

    // Case 1: Loading just completed (transitions from true to false)
    const loadingJustCompleted = prevLoadingRef.current && !loading

    // Case 2: Modal opened with data already available (loading was never true)
    const modalJustOpenedWithData = !prevOpenRef.current && !loading

    if ((loadingJustCompleted || modalJustOpenedWithData) && currentExperiences) {
      const experienceIds = currentExperiences.map((exp) => String(exp.id))
      setSelectedExperiences(experienceIds)
      form.setFieldsValue({ experiences: experienceIds })
    }

    // Update refs AFTER checking conditions
    prevOpenRef.current = true
    prevLoadingRef.current = loading
  }, [open, loading, currentExperiences, form])

  const handleRemoveExperience = (expId) => {
    // Prevent removal if this is the only experience
    if (selectedExperiences.length === 1) {
      return
    }

    setSelectedExperiences((prev) => {
      const next = prev.filter((id) => id !== expId)
      form.setFieldsValue({ experiences: next })
      return next
    })
  }

  const handleSubmit = async () => {
    if (!onSave) {
      return
    }

    setIsSubmitting(true)

    try {
      await onSave(selectedExperiences)
      onCancel()
    } catch (error) {
      // Check if this is a booking conflict error
      if (error?.isBookingConflict) {
        // Don't show error toast for booking conflicts - parent will handle via conflict modal
        // Don't call onCancel() - let parent close this modal and show conflict modal instead
        return
      }

      // For other errors, show error message
      console.error("Error assigning experiences:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setSelectedExperiences([])
    onCancel()
  }

  const columns = [
    {
      title: t("equipment.details.columns.experience_name"),
      dataIndex: "name",
      key: "name",
      // ellipsis: true,
      render: (name) => <Text fw="500">{name}</Text>
    },
    {
      title: t("equipment.details.columns.guests"),
      dataIndex: "guests",
      key: "guests",
      align: "center",
      render: (guests) => <Text>{guests}</Text>
    },
    {
      title: t("equipment.details.columns.price"),
      key: "price",
      align: "right",
      render: (_, record) => (
        <Text fw="400">
          {record.currency}
          {record.price.toFixed(2)}
        </Text>
      )
    },
    {
      title: t("equipment.details.columns.status"),
      dataIndex: "status",
      key: "status",
      align: "left",
      render: (status) => {
        const isPublished = String(status).toLowerCase() === "published"
        const displayText = String(status).charAt(0).toUpperCase() + String(status).slice(1)
        return (
          <Tag color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null}>
            {displayText}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, record) => {
        const isOnlyExperience = selectedExperienceData.length === 1
        return (
          <Button
            type="text"
            icon={<DeleteOutlined className={styles.deleteIcon} />}
            onClick={() => handleRemoveExperience(record.id)}
            className={styles.removeButton}
            aria-label="Remove experience"
            disabled={isOnlyExperience}
            title={
              isOnlyExperience
                ? t("equipment.details.cannot_remove_last_experience", { defaultValue: "Cannot remove the last experience" })
                : ""
            }
          />
        )
      }
    }
  ]

  const modalTitle = (
    <Title level={4}>
      {t("equipment.modals.assign_experiences", { defaultValue: "Assign Experiences" })}{" "}
      <span className={styles.experiencesCount}>({selectedExperienceData.length})</span>
    </Title>
  )

  const modalContent = loading ? (
    <Flex justify="center" align="center" style={{ padding: "40px 0" }}>
      <Spin size="large" />
    </Flex>
  ) : (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Flex vertical gap={spacings.M}>
        <LabeledField
          name="experiences"
          labelKey={t("equipment.experiences", { defaultValue: "Experiences" })}
          rules={[
            { required: true, message: t("equipment.experience_required", { defaultValue: "Please select at least one experience" }) }
          ]}
        >
          <ExperienceSelector
            experiences={experiencesMemo}
            value={selectedExperiences}
            onChange={(vals) => {
              const strVals = vals.map(String)
              setSelectedExperiences(strVals)
              form.setFieldsValue({ experiences: strVals })
            }}
          />
        </LabeledField>

        {selectedExperienceData.length > 0 && (
          <div>
            <Title level={5} className={styles.assignedTitle}>
              {t("equipment.assigned_to_experiences", { defaultValue: "Assigned Experiences" })}
            </Title>
            <div className={styles.tableWrapper}>
              <Table
                dataSource={selectedExperienceData}
                rowKey="id"
                pagination={false}
                columns={columns}
                className={styles.experiencesTable}
              />
            </div>
          </div>
        )}

        {selectedExperienceData.length === 0 && (
          <Text type="secondary" className={styles.emptyText}>
            {emptyText || t("equipment.no_experience_assigned", { defaultValue: "No experience assigned." })}
          </Text>
        )}

        <Flex gap={spacings.S} justify="flex-end" className={styles.footerActions}>
          <Button onClick={handleCancel} className={styles.cancelBtn}>
            {t("common.cancel")}
          </Button>
          <Button type="primary" htmlType="submit" loading={isSubmitting} className={styles.saveBtn}>
            {t("common.save")}
          </Button>
        </Flex>
      </Flex>
    </Form>
  )

  return isMobile ? (
    <CustomDrawer
      title={modalTitle}
      open={open}
      setOpen={onCancel}
      className={styles.assignExperiencesModal}
    >
      {modalContent}
    </CustomDrawer>
  ) : (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={handleCancel}
      className={styles.assignExperiencesModal}
      width={800}
      footer={null}
      maskClosable={false}
    >
      {modalContent}
    </Modal>
  )
}

AssignExperiencesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentExperiences: PropTypes.array,
  emptyText: PropTypes.string,
  loading: PropTypes.bool
}

export default AssignExperiencesModal
